import { useCallback, useEffect, useRef, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Handles the logic for a quantity stepper component, including visibility toggling,
 * quantity updates, and callback handling for external events.
 */
export const useQuantityStepperV2 = (params: Params) => {
  const {
    initialQuantity = 1,
    shouldResetOnTimeout,
    onChange,
    onIncrease,
    onDecrease,
    onShowStepper,
    onHideStepper,
  } = params;

  // ─── Refs ────────────────────────────────────────────────────────────

  // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
  const visibilityTimeout = useRef<ReturnType<typeof setTimeout>>();

  // ─── State ───────────────────────────────────────────────────────────

  const [isStepperShown, setIsStepperShown] = useState(false);
  const [currentQuantity, setCurrentQuantity] = useState(initialQuantity);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const showStepper = useCallback(() => {
    onShowStepper?.();
    setIsStepperShown(true);
  }, [onShowStepper]);

  const hideStepper = useCallback(() => {
    onHideStepper?.();
    setIsStepperShown(false);
  }, [onHideStepper]);

  const increaseQuantity = useCallback(() => {
    setCurrentQuantity((quantity) => {
      const updatedQty = quantity + 1;

      onIncrease?.(updatedQty);

      return updatedQty;
    });
  }, [onIncrease]);

  const decreaseQuantity = useCallback(() => {
    setCurrentQuantity((quantity) => {
      const updatedQty = quantity - 1;

      if (updatedQty < 0) {
        return quantity;
      }

      if (updatedQty === 0) {
        hideStepper();
        onDecrease?.(updatedQty);

        return initialQuantity;
      }

      onDecrease?.(updatedQty);

      return updatedQty;
    });
  }, [hideStepper, initialQuantity, onDecrease]);

  const onTimeout = useCallback(() => {
    hideStepper();
    onChange(currentQuantity);

    if (shouldResetOnTimeout) {
      setCurrentQuantity(initialQuantity);
    }
  }, [
    currentQuantity,
    hideStepper,
    initialQuantity,
    onChange,
    shouldResetOnTimeout,
  ]);

  /**
   * A callback function that manages the visibility timeout logic for a component.
   * Clears any existing timeout before setting a new one when a condition is met.
   * Ensures cleanup by clearing the timeout when the function is unmounted or re-invoked.
   */
  const handleVisibilityTimeout = useCallback(() => {
    if (visibilityTimeout.current) {
      clearTimeout(visibilityTimeout.current);
    }

    if (isStepperShown) {
      visibilityTimeout.current = setTimeout(onTimeout, STEPPER_VISIBILITY_TIMEOUT); // prettier-ignore
    }

    return () => {
      clearTimeout(visibilityTimeout.current);
    };
  }, [isStepperShown, onTimeout]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(handleVisibilityTimeout, [handleVisibilityTimeout]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    isStepperShown,
    quantity: currentQuantity,
    showStepper,
    hideStepper,
    increaseQuantity,
    decreaseQuantity,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const STEPPER_VISIBILITY_TIMEOUT = 2000;

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  initialQuantity?: number;
  shouldResetOnTimeout?: boolean;
  onChange: (quantity: number) => void;
  onIncrease?: (quantity: number) => void;
  onDecrease?: (quantity: number) => void;
  onShowStepper?: () => void;
  onHideStepper?: () => void;
};
